import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../hooks/useToast';
import axios from 'axios';
import ChatModal from './ChatModal';
import { extractPrimaryImage } from '../utils/imageHelpers';

const BuyerOffersDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedStatus, setSelectedStatus] = useState('pending');
  const [stats, setStats] = useState({});
  const [payingFee, setPayingFee] = useState(null);
  const [chatModal, setChatModal] = useState({ isOpen: false, offer: null });

  // Handle fee payment
  const handlePayFee = async (offerId, feeAmount = 1) => {
    // TODO: Replace window.confirm with a custom confirmation dialog
    if (!window.confirm(`Pay ${feeAmount} TND to unlock chat with the seller?`)) {
      return;
    }

    try {
      setPayingFee(offerId);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/second-hand-offers/${offerId}/pay-fee`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        toast.success('Fee paid successfully! Chat is now unlocked.');
        fetchOffers(currentPage, selectedStatus);
        fetchStats();
      } else {
        toast.error(response.data.message || 'Failed to pay fee');
      }
    } catch (error) {
      console.error('Error paying fee:', error);
      toast.error(error.response?.data?.message || 'Failed to pay fee');
    } finally {
      setPayingFee(null);
    }
  };

  // Fetch offers made by buyer
  const fetchOffers = async (page = 1, status = 'pending') => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '10'
      });
      
      if (status && status !== 'all') {
        params.append('status', status);
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/second-hand-offers/made?${params}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setOffers(response.data.data);
        setTotalPages(response.data.pagination.totalPages || 1);
        setCurrentPage(response.data.pagination.page || 1);
      }
    } catch (error) {
      console.error('Error fetching offers:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch offer statistics
  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/second-hand-offers/stats`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        setStats(response.data.data.user || {});
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    fetchOffers(1, selectedStatus);
    fetchStats();
  }, [selectedStatus]);

  const handleStatusFilter = (status) => {
    setSelectedStatus(status);
    setCurrentPage(1);
    fetchOffers(1, status);
  };

  const handlePageChange = (page) => {
    fetchOffers(page, selectedStatus);
  };

  const handleWithdrawOffer = async (offerId) => {
    // TODO: Replace window.confirm with a custom confirmation dialog
    if (!window.confirm('Are you sure you want to withdraw this offer?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/second-hand-offers/${offerId}/withdraw`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        toast.success('Offer withdrawn successfully!');
        fetchOffers(currentPage, selectedStatus);
        fetchStats();
      } else {
        toast.error(response.data.message || 'Failed to withdraw offer');
      }
    } catch (error) {
      console.error('Error withdrawing offer:', error);
      toast.error(error.response?.data?.message || 'Failed to withdraw offer');
    }
  };

  const formatPrice = (price) => `${new Intl.NumberFormat('en-US').format(price ?? 0)} TND`;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'text-yellow-400 bg-yellow-400/10',
      accepted: 'text-green-400 bg-green-400/10',
      rejected: 'text-red-400 bg-red-400/10',
      expired: 'text-gray-400 bg-gray-400/10',
      withdrawn: 'text-gray-400 bg-gray-400/10'
    };
    return colors[status] || 'text-gray-400 bg-gray-400/10';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: '⏳',
      accepted: '✅',
      rejected: '❌',
      expired: '⏰',
      withdrawn: '🚫'
    };
    return icons[status] || '❓';
  };

  const getDaysUntilExpiration = (expiresAt) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const timeDiff = expires - now;
    const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
    return Math.max(0, daysDiff);
  };

  const getCountdown = (offer) => {
  if (offer.isInfinite) return '∞';
    if (!offer.expiresAt) return 'Pending activation';
    const now = Date.now();
    const end = new Date(offer.expiresAt).getTime();
    const diff = end - now;
    if (diff <= 0) return 'Expired';
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return hours > 72 ? `${getDaysUntilExpiration(offer.expiresAt)}d` : `${hours}h ${minutes}m`;
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Please Login</h2>
          <p className="text-gray-400">You need to be logged in to view your offers.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">📤 My Offers</h1>
          <p className="text-gray-400">Track your offers on second-hand products</p>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="text-2xl text-blue-400 mb-2">📤</div>
            <div className="text-2xl font-bold text-white">{stats.offersMade || 0}</div>
            <div className="text-gray-400 text-sm">Total Made</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="text-2xl text-yellow-400 mb-2">⏳</div>
            <div className="text-2xl font-bold text-white">{stats.pendingMade || 0}</div>
            <div className="text-gray-400 text-sm">Pending</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="text-2xl text-orange-400 mb-2">📬</div>
            <div className="text-2xl font-bold text-white">{stats.offersReceived || 0}</div>
            <div className="text-gray-400 text-sm">Received on Your Products</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
            <div className="text-2xl text-purple-400 mb-2">📥</div>
            <div className="text-2xl font-bold text-white">{stats.pendingReceived || 0}</div>
            <div className="text-gray-400 text-sm">Pending on Your Products</div>
          </div>
        </div>

        {/* Status Filter */}
        <div className="bg-gray-800 rounded-lg p-4 mb-6 border border-gray-700">
          <div className="flex flex-wrap gap-2">
            {['pending', 'accepted', 'rejected', 'expired', 'withdrawn', 'all'].map((status) => (
              <button
                key={status}
                onClick={() => handleStatusFilter(status)}
                className={`px-4 py-2 rounded-lg transition-all duration-300 ${
                  selectedStatus === status
                    ? 'bg-yellow-500 text-black font-medium'
                    : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Offers List */}
        {loading ? (
          <div className="bg-gray-800 rounded-lg p-8 text-center border border-gray-700">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400 mx-auto mb-4"></div>
            <p className="text-gray-400">Loading your offers...</p>
          </div>
        ) : offers.length > 0 ? (
          <div className="space-y-4">
            {offers.map((offer) => (
              <div key={offer._id} className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                  {/* Offer Info */}
                  <div className="flex-1 mb-4 lg:mb-0">
                    <div className="flex items-start space-x-4">
                      {/* Product Image */}
                      <div className="w-16 h-16 bg-gray-700 rounded-lg flex items-center justify-center overflow-hidden">
                        {(() => {
                          const { url, thumbnailUrl } = extractPrimaryImage(offer.productId);
                          const imageSrc = thumbnailUrl || url;
                          return imageSrc ? (
                            <img
                              src={imageSrc}
                              alt={offer.productId?.name || 'Product image'}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <span className="text-2xl">📦</span>
                          );
                        })()}
                      </div>
                      
                      {/* Details */}
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h3 className="text-lg font-semibold text-white">
                            {offer.productId?.name || 'Product'}
                          </h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(offer.status)}`}>
                            {getStatusIcon(offer.status)} {offer.status.toUpperCase()}
                          </span>
                        </div>
                        
                        <div className="space-y-1 text-sm text-gray-400">
                          <p>
                            <span className="font-medium">Your Offer:</span> 
                            <span className="text-yellow-400 font-bold ml-1">
                              {formatPrice(offer.offerAmount)}
                            </span>
                          </p>
                          <p>
                            <span className="font-medium">Reserve Price:</span> {formatPrice(offer.productId?.reservePrice || 0)}
                          </p>
                          <p>
                            <span className="font-medium">Seller:</span> {offer.sellerId?.username || 'Anonymous'}
                          </p>
                          <p>
                            <span className="font-medium">Date:</span> {formatDate(offer.createdAt)}
                          </p>
                          {offer.status === 'pending' && (
                            <p>
                              <span className="font-medium">Time Left:</span>
                              <span className="text-orange-400 ml-1">{getCountdown(offer)}</span>
                            </p>
                          )}
                          {offer.message && (
                            <p>
                              <span className="font-medium">Your Message:</span> 
                              <span className="text-gray-300 italic ml-1">"{offer.message}"</span>
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="lg:ml-6">
                    {offer.status === 'pending' && (
                      <div className="mb-3">
                        <button
                          onClick={() => handleWithdrawOffer(offer._id)}
                          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-300 text-sm"
                        >
                          🚫 Withdraw Offer
                        </button>
                      </div>
                    )}
                    
                    {(offer.status === 'accepted' || offer.chatUnlocked || offer.feePaid || offer.buyerFeePaid || (offer.status === 'pending' && offer.provisionalAccepted)) && (
                      <div className="space-y-3 mb-3">
                        {(offer.status === 'accepted' || offer.chatUnlocked || offer.feePaid || offer.buyerFeePaid) ? (
                          <div className="bg-green-600/20 border border-green-600/50 rounded-lg p-3 text-center">
                            <div className="text-green-400 font-semibold">✅ Chat Unlocked!</div>
                            <div className="text-xs text-gray-400 mt-1">You can now contact the seller</div>
                            <button
                              onClick={() => setChatModal({ isOpen: true, offer })}
                              className="mt-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-300 text-sm"
                            >
                              � Start Chat
                            </button>
                          </div>
                        ) : (
                          <div className="bg-yellow-600/20 border border-yellow-600/50 rounded-lg p-3">
                            <div className="text-yellow-400 font-semibold text-sm mb-2">⏳ Waiting for seller acceptance</div>
                            <div className="text-xs text-gray-300">
                              {offer.buyerFeeHeld ? `Your ${formatPrice(offer.feeAmount || 1)} hold is in place and will transfer automatically once the seller accepts.` : 'Awaiting seller action.'}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {offer.status === 'rejected' && offer.sellerResponse && (
                      <div className="bg-red-600/20 border border-red-600/50 rounded-lg p-3 max-w-xs mb-3">
                        <div className="text-red-400 font-semibold text-sm mb-1">❌ Offer Rejected</div>
                        <p className="text-xs text-gray-300">"{offer.sellerResponse}"</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {formatDate(offer.respondedAt)}
                        </p>
                      </div>
                    )}

                    {/* View Details Link */}
                    <button
                      onClick={() => {
                        navigate(`/offers/${offer._id}`);
                      }}
                      className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-300 text-sm"
                    >
                      👁️ View Details
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-gray-800 rounded-lg p-8 text-center border border-gray-700">
            <div className="text-6xl mb-4">📭</div>
            <h3 className="text-xl font-semibold text-white mb-2">No Offers Found</h3>
            <p className="text-gray-400">
              {selectedStatus === 'pending' 
                ? "You don't have any pending offers at the moment."
                : `No ${selectedStatus} offers found.`
              }
            </p>
            <p className="text-gray-400 mt-2">
              Visit the <a href="/second-hand" className="text-yellow-400 hover:underline">Second Hand Market</a> to make your first offer!
            </p>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center space-x-4 mt-8">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300"
            >
              ← Previous
            </button>
            
            <div className="flex space-x-2">
              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i + 1}
                  onClick={() => handlePageChange(i + 1)}
                  className={`w-10 h-10 rounded-lg font-medium transition-colors duration-300 ${
                    currentPage === i + 1
                      ? 'bg-yellow-500 text-black'
                      : 'bg-gray-700 hover:bg-gray-600 text-white'
                  }`}
                >
                  {i + 1}
                </button>
              ))}
            </div>
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300"
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {/* Chat Modal */}
      <ChatModal
        isOpen={chatModal.isOpen}
        onClose={() => setChatModal({ isOpen: false, offer: null })}
        offer={chatModal.offer}
        currentUser={user}
      />
    </div>
  );
};

export default BuyerOffersDashboard;
